import { Bell, BellRing, ChevronRight, Info, Settings2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, Card, EmptyState, Sheet } from '../../components/ui'
import type { LocalNotificationRecord } from '../../data/local/db'
import { NotificationRepository } from '../../data/local/notificationRepository'
import type { NotificationPreference } from '../../domain/notification'
import {
  notificationRepository as defaultRepository,
  subscribeSyncRuntime,
  syncNow,
} from '../../services/syncRuntime'
import {
  detectRuntimePushCapability,
  isIosBrowser,
  requestBestEffortPush,
  type PushAttemptResult,
} from '../../services/push'
import { sessionStore } from '../access/sessionStore'

export function NotificationCenter({
  prominent = false,
  repository = defaultRepository,
  pushRequester = requestBestEffortPush,
}: {
  prominent?: boolean
  repository?: NotificationRepository
  pushRequester?: () => Promise<PushAttemptResult>
}) {
  const navigate = useNavigate()
  const session = sessionStore((state) => state.session)
  const [open, setOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [notifications, setNotifications] = useState<LocalNotificationRecord[]>([])
  const [preference, setPreference] = useState<NotificationPreference | null>(null)
  const [pushMessage, setPushMessage] = useState<string | null>(null)
  const [pushBusy, setPushBusy] = useState(false)

  const refresh = useCallback(async () => {
    if (!session) return
    const [visible, currentPreference] = await Promise.all([
      repository.listVisible(session.memberId),
      repository.getPreference(session.tripId, session.memberId),
    ])
    setNotifications(visible)
    setPreference(currentPreference)
  }, [repository, session])

  useEffect(() => {
    const initialRefresh = window.setTimeout(() => void refresh(), 0)
    const pendingTimer = window.setInterval(() => void refresh(), 60_000)
    const unsubscribe = subscribeSyncRuntime(() => void refresh())
    return () => {
      window.clearTimeout(initialRefresh)
      window.clearInterval(pendingTimer)
      unsubscribe()
    }
  }, [refresh])

  const unread = notifications.filter((item) => item.readAt === null).length
  const capability = detectRuntimePushCapability()
  async function openNotification(item: LocalNotificationRecord): Promise<void> {
    if (!session) return
    await repository.markRead(item.id, session.memberId)
    if (navigator.onLine) await syncNow()
    setOpen(false)
    setSettingsOpen(false)
    await refresh()
    void navigate(item.deepLink)
  }

  async function toggleSocial(enabled: boolean): Promise<void> {
    if (!session) return
    await repository.setSocialEnabled(session.tripId, session.memberId, enabled)
    if (navigator.onLine) await syncNow()
    await refresh()
  }

  async function activatePush(openSettingsAfter = false): Promise<void> {
    if (!session) return
    setPushBusy(true)
    const result = await pushRequester()
    await repository.setPushStatus(session.tripId, session.memberId, result.status)
    setPushMessage(result.status === 'granted'
      ? 'Rappels activés.'
      : result.status === 'denied'
        ? 'Rappels refusés. Tu peux les autoriser plus tard dans les réglages du navigateur.'
        : result.status === 'requires_install'
          ? 'Ajoute d’abord Caravan à l’écran d’accueil.'
          : 'Impossible de les activer ici pour le moment.')
    setPushBusy(false)
    await refresh()
    if (openSettingsAfter) {
      setOpen(true)
      setSettingsOpen(true)
    }
  }

  return (
    <>
      <div className="notification-tools">
        {prominent && (capability === 'default' || capability === 'requires_install') ? (
          <Button className="notification-opt-in" disabled={pushBusy} variant="secondary" onClick={() => void activatePush(true)}>
            <Bell size={17} /> {pushBusy ? 'Activation…' : capability === 'requires_install' ? 'Installer pour les rappels' : 'Activer'}
          </Button>
        ) : null}
        <Button className="notification-trigger" aria-label="Ouvrir les notifications" variant="ghost" onClick={() => { setOpen(true); void refresh() }}>
          {unread > 0 ? <BellRing size={19} /> : <Bell size={19} />}
          {unread > 0 ? <span>{unread}</span> : null}
        </Button>
      </div>
      {open ? (
        <Sheet title="Notifications" onClose={() => { setOpen(false); setSettingsOpen(false) }}>
          <div className="notification-center">
            <header>
              <p>Rendez-vous, défis, votes et nouvelles réactions.</p>
              <Button variant="ghost" onClick={() => setSettingsOpen((value) => !value)}><Settings2 size={16} /> Réglages</Button>
            </header>
            {settingsOpen ? (
              <Card className="notification-settings">
                <h3>Réglages</h3>
                <label>
                  <input
                    checked={preference?.socialEnabled ?? true}
                    type="checkbox"
                    onChange={(event) => void toggleSocial(event.target.checked)}
                  />
                  <span><strong>Photos, votes et défis</strong><small>Préviens-moi quand quelque chose se passe.</small></span>
                </label>
                <p><Info size={15} /> Les rendez-vous importants restent toujours visibles ici.</p>
                {isIosBrowser() ? <p className="ios-push-guide">Sur iPhone, ajoute d’abord Caravan à l’écran d’accueil.</p> : null}
                <Button disabled={pushBusy} variant="secondary" onClick={() => void activatePush()}>
                  <Bell size={16} /> {pushBusy ? 'Activation…' : 'Activer les rappels'}
                </Button>
                {pushMessage ? <p className="push-message" role="status">{pushMessage}</p> : null}
              </Card>
            ) : null}
            <div className="notification-list">
              {notifications.length === 0 ? <EmptyState title="Rien pour le moment" detail="Les prochains rappels apparaîtront ici." /> : null}
              {notifications.map((item) => (
                <button className={item.readAt ? 'notification-item is-read' : 'notification-item'} key={item.id} type="button" onClick={() => void openNotification(item)}>
                  <span className="notification-item__icon">{item.importance === 'important' ? <BellRing size={17} /> : <Bell size={17} />}</span>
                  <span><strong>{item.title}</strong><small>{item.body}</small></span>
                  <ChevronRight size={16} />
                </button>
              ))}
            </div>
          </div>
        </Sheet>
      ) : null}
    </>
  )
}
